"use client";
import { useMemo, useState } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useT, useI18n } from "@/lib/i18n";
import { useApi } from "@/lib/hooks/use-api";
import { useAuth } from "@/lib/auth/provider";
import { api, post } from "@/lib/api/client";
import {
  Badge,
  Button,
  DataState,
  Empty,
  PageHeading,
  Progress,
} from "@/components/ui";
import {
  BookOpen,
  CheckCircle2,
  ChevronRight,
  FlaskConical,
  Loader2,
  PlayCircle,
  Star,
  Target,
} from "lucide-react";

/* ------------------------------ Түрлер ------------------------------ */

interface CourseItem {
  id: string;
  title: string;
  description: string;
  level: string;
  track: string;
  status: string;
  modules: number;
  lessons: number;
  source_locale?: string;
}

interface ModuleDetail {
  id: string;
  title: string;
  lessons: {
    id: string;
    title: string;
    objective: string;
    minutes: number;
    xp: number;
    labTemplateId?: string;
  }[];
}

interface StepDto {
  id: string;
  pos: number;
  type: string;
  title: string;
  content: Record<string, any>;
  status: string;
  /** PHASE 1: content қай тілден келді (аударылмаған болса "kk") */
  source_locale?: string;
}

interface LessonDto {
  lesson: {
    id: string;
    title: string;
    objective: string;
    minutes: number;
    xp: number;
    labTemplateId?: string;
  };
  status: string;
  xp_earned: number;
  lab_session_id: string | null;
  steps: StepDto[];
}
/** Аударылмаған контентті «басқа тілде» деп көрсетеді (тихия подмена жоқ). */
function SourceFlag({ source, lang }: { source?: string; lang: string }) {
  if (!source || source === lang) return null;
  const names: Record<string, string> = { kk: "қазақша", ru: "орысша", en: "english", ko: "한국어" };
  return <span className="source-locale-flag">Түпнұсқа: {names[source] ?? source}</span>;
}

const STEP_LABELS: Record<string, string> = {
  THEORY: "Теория",
  VIDEO: "Видео",
  QUIZ_SINGLE: "Тест (1 жауап)",
  QUIZ_MULTI: "Тест (көп жауап)",
  CONFIG_FIND: "Қатені тап",
  LOG_ANALYSIS: "Лог талдауы",
  SHORT_ANSWER: "Қысқа жауап",
  FIND_VULN: "Осалдықты талда",
  REPORT: "Есеп",
  LAB_TASK: "Зертхана тапсырмасы",
  SCRIPT_REVIEW: "Скрипт-бағалау",
};

/* ------------------------- Курстар тізімі --------------------------- */

export function CoursesPage() {
  const t = useT();
  const auth = useAuth();
  const courses = useApi<{ items: CourseItem[] }>(
    auth.authenticated ? "/courses" : null,
  );
  const [open, setOpen] = useState<string>();
  const detail = useApi<ModuleDetail[]>(
    open ? `/courses/${open}` : null,
  );

  return (
    <div className="page">
      <PageHeading
        eyebrow={t("courses.page_eyebrow")}
        title={t("courses.page_title")}
        description={t("courses.page_desc")}
      />
      {!auth.authenticated ? (
        <Empty
          title={t("courses.guest_title")}
          text={t("courses.guest_text")}
          action={
            <Link className="button primary" href="/login">
              {t("auth.login_btn")}
            </Link>
          }
        />
      ) : (
      <DataState
        loading={courses.loading}
        error={courses.error}
        retry={courses.reload}
      >
        {courses.data?.items.length === 0 ? (
          <Empty title={t("courses.empty_title")} />
        ) : (
        <div className="course-list">
          {courses.data?.items.map((c) => (
            <div key={c.id} className="card course-card">
              <button
                className="course-card-head"
                onClick={() => setOpen(open === c.id ? undefined : c.id)}
                aria-expanded={open === c.id}
              >
                <div className="course-icon">
                  <BookOpen size={22} />
                </div>
                <div className="course-meta">
                  <h3>{c.title}</h3>
                  <p>{c.description}</p>
                  <div className="inline" style={{ marginTop: 8 }}>
                    <Badge value={c.status} />
                    <span className="muted small">
                      {c.level} · {c.modules} модуль · {c.lessons} сабақ
                    </span>
                  </div>
                </div>
                <ChevronRight
                  size={20}
                  className={open === c.id ? "chev open" : "chev"}
                />
              </button>
              {open === c.id && (
                <div className="course-detail">
                  {detail.loading && <Loader2 size={18} className="spin" />}
                  {detail.error && (
                    <p className="muted">{detail.error.message}</p>
                  )}
                  {detail.data?.map((m) => (
                    <div key={m.id} className="module-block">
                      <h4 className="module-title">{m.title}</h4>
                      <div className="lesson-rows">
                        {m.lessons.map((l) => (
                          <Link
                            key={l.id}
                            href={`/lessons/${l.id}`}
                            className="lesson-row"
                          >
                            <PlayCircle size={18} className="lesson-play" />
                            <div>
                              <strong>{l.title}</strong>
                              <div className="muted small">
                                {l.objective}
                              </div>
                            </div>
                            <span className="lesson-xp">
                              <Star size={13} /> {l.xp} XP · {l.minutes} мин
                            </span>
                          </Link>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
        )}
      </DataState>
      )}
    </div>
  );
}

/* --------------------------- Сабақ ойнағышы --------------------------- */

export function LessonPage({ id }: { id: string }) {
  const t = useT();
  const { lang } = useI18n();
  const lesson = useApi<LessonDto>(`/lessons/${id}`);
  const [submitted, setSubmitted] = useState<Record<string, { status: string; feedback: string }>>({});
  const [choice, setChoice] = useState<Record<string, number[]>>({});
  const [text, setText] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [page, setPage] = useState(0);
  const [labBusy, setLabBusy] = useState(false);
  const [labError, setLabError] = useState<string | null>(null);
  const router = useRouter();

  // LAB_TASK: открыть персональную комнату полигона (тот же поток, что и на дашборде).
  async function openPersonalLab() {
    setLabBusy(true);
    setLabError(null);
    try {
      const res = await post<{ session_id: string }>("/sessions", {
        scenario_id: "personal-kali",
      });
      const sess = await api<{
        lab?: { id: string; status: string };
      }>(`/sessions/${encodeURIComponent(res.session_id)}`);
      const l = sess.lab;
      if (
        l &&
        ["STOPPED", "ERROR", "CREATING", "READY", "STOPPING"].includes(
          l.status,
        )
      ) {
        try {
          await post(`/labs/${encodeURIComponent(l.id)}/start`);
        } catch {
          /* комната сама дождётся RUNNING */
        }
      }
      if (l) {
        void document.documentElement.requestFullscreen?.().catch(() => {});
        router.push(`/lab/${encodeURIComponent(l.id)}`);
      } else {
        setLabError(t("courses.lab_no_lab"));
      }
    } catch (e) {
      setLabError(
        (e as Error).message || "Не удалось запустить лабораторию.",
      );
    } finally {
      setLabBusy(false);
    }
  }

  const data = lesson.data;
  const current =
    data?.steps.find((s, i) => i === page) ?? data?.steps[0];
  const doneCount = data?.steps.filter((s) => s.status === "PASSED").length ?? 0;
  const pct = data ? Math.round((doneCount / data.steps.length) * 100) : 0;

  async function submit() {
    if (!data || !current) return;
    setBusy(true);
    try {
      const c = current.content as Record<string, any>;
      let answer: unknown;
      if (current.type === "QUIZ_MULTI")
        answer = choice[current.id] ?? [];
      else if (
        ["QUIZ_SINGLE", "CONFIG_FIND", "FIND_VULN", "SCRIPT_REVIEW"].includes(
          current.type,
        )
      )
        answer = (choice[current.id] ?? [])[0] ?? -1;
      else if (current.type === "REPORT")
        answer = { text: text[current.id] ?? "" };
      else answer = text[current.id] ?? "";
      const res = await post<{ status: string; feedback: string }>(
        `/lessons/${id}/steps/${current.id}`,
        { answer },
      );
      setSubmitted((p) => ({ ...p, [current.id]: res }));
      lesson.reload();
      // VIDEO/THEORY — жауап сұралмайды: сәтті тапсырылса автоматты келесі қадамға
      if (
        (current.type === "VIDEO" || current.type === "THEORY") &&
        res.status === "PASSED" &&
        page < data.steps.length - 1
      ) {
        window.setTimeout(() => setPage((p) => p + 1), 650);
      }
    } catch (e) {
      setSubmitted((p) => ({
        ...p,
        [current.id]: {
          status: "FAILED",
          feedback: (e as Error).message,
        },
      }));
    } finally {
      setBusy(false);
    }
  }

  const isQuiz = (t: string) =>
    ["QUIZ_SINGLE", "CONFIG_FIND", "FIND_VULN"].includes(t);
  const isMulti = (t: string) => t === "QUIZ_MULTI";

  return (
    <div className="page">
      <PageHeading
        eyebrow={t("courses.lesson_eyebrow2")}
        title={data?.lesson.title ?? t("courses.lesson_eyebrow2")}
        description={data?.lesson.objective}
      />
      <DataState
        loading={!data && lesson.loading}
        error={lesson.error}
        retry={lesson.reload}
      >
        {data && current ? (
          <>
            <div
              className="inline"
              style={{
                justifyContent: "space-between",
                marginBottom: "14px",
              }}
            >
              <div className="inline">
                <Badge value={data.status === "COMPLETED" ? "COMPLETED" : "IN_PROGRESS"} />
                <span className="muted small">
                  {data.lesson.minutes} минут ·{" "}
                  <Star size={12} style={{ verticalAlign: -2 }} /> {data.lesson.xp} XP
                  {data.xp_earned > 0 && ` (алынды: ${data.xp_earned})`}
                </span>
              </div>
              <div style={{ width: 220 }}>
                <Progress value={pct} label={`${doneCount}/${data.steps.length} қадам`} />
              </div>
            </div>

            <div className="card lesson-card">
              <div className="inline" style={{ marginBottom: 12 }}>
                <Badge value={current.status} />
                <span className="muted small">
                  Қадам {page + 1}/{data.steps.length} · {STEP_LABELS[current.type] ?? current.type}
                </span>
              </div>
              <h3 className="lesson-title">
                {current.title} <SourceFlag source={current.source_locale} lang={lang} />
              </h3>

              {current.type === "THEORY" && (
                <div className="lesson-theory">
                  {((current.content as any).paragraphs as string[] | undefined)?.map(
                    (p, i) => (
                      <p key={i}>{p}</p>
                    ),
                  )}
                  {((current.content as any).text as string | undefined) && (
                    <pre className="lesson-pre theory-text">
                      {(current.content as any).text as string}
                    </pre>
                  )}
                  {((current.content as any).terms as { t: string; d: string }[] |
                    undefined)?.map((term, i) => (
                    <div key={i} className="term-row">
                      <strong>{term.t}</strong> — <span>{term.d}</span>
                    </div>
                  ))}
                </div>
              )}

              {current.type === "VIDEO" && (() => {
                const c = current.content as any;
                const vid: string =
                  c.video?.[lang] ?? c.video?.ru ?? c.video?.en ?? "";
                return (
                  <div className="lesson-video">
                    {c.intro && <p className="video-intro">{c.intro}</p>}
                    {vid ? (
                      <div className="video-frame">
                        <iframe
                          src={`https://www.youtube-nocookie.com/embed/${vid}?rel=0`}
                          title={c.videoTitle ?? "Видео"}
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        />
                      </div>
                    ) : (
                      <p className="muted">Видео қолжетімсіз.</p>
                    )}
                    {c.note && <p className="video-note">{c.note}</p>}
                  </div>
                );
              })()}

              {(isQuiz(current.type) || isMulti(current.type)) && (
                <div className="quiz-block">
                  <p className="quiz-q">
                    {(current.content as any).question as string}
                  </p>
                  {((current.content as any).options as string[]).map(
                    (opt, i) => {
                      const selected = (choice[current.id] ?? []).includes(i);
                      return (
                        <label key={i} className="quiz-opt">
                          <input
                            type={isMulti(current.type) ? "checkbox" : "radio"}
                            name={current.id}
                            checked={selected}
                            onChange={() =>
                              setChoice((p) => {
                                const cur = p[current.id] ?? [];
                                const next = isMulti(current.type)
                                  ? selected
                                    ? cur.filter((v) => v !== i)
                                    : [...cur, i]
                                  : [i];
                                return { ...p, [current.id]: next };
                              })
                            }
                          />
                          <span>{opt}</span>
                        </label>
                      );
                    },
                  )}
                </div>
              )}

              {(current.type === "LOG_ANALYSIS" ||
                current.type === "CONFIG_FIND") &&
                (current.content as any).pre && (
                  <pre className="lesson-pre">{(current.content as any).pre}</pre>
                )}

              {(current.type === "LOG_ANALYSIS" ||
                current.type === "SHORT_ANSWER") && (
                <div className="lesson-input">
                  <p className="quiz-q">
                    {((current.content as any).question ??
                      (current.content as any).prompt) as string}
                  </p>
                  <input
                    className="input"
                    value={text[current.id] ?? ""}
                    onChange={(e) =>
                      setText((p) => ({ ...p, [current.id]: e.target.value }))
                    }
                    placeholder="Жауабыңыз..."
                  />
                </div>
              )}

              {current.type === "REPORT" && (
                <div className="lesson-input">
                  <p className="quiz-q">
                    {(current.content as any).prompt as string}
                  </p>
                  <textarea
                    className="input"
                    rows={5}
                    value={text[current.id] ?? ""}
                    onChange={(e) =>
                      setText((p) => ({ ...p, [current.id]: e.target.value }))
                    }
                    placeholder={t("courses.report_ph")}
                  />
                </div>
              )}

              {current.type === "LAB_TASK" && (
                <div className="lesson-lab">
                  <div className="inline" style={{ gap: 8 }}>
                    <FlaskConical size={18} />
                    <strong>{t("courses.lab_title")}</strong>
                  </div>
                  <p>{(current.content as any).note as string}</p>
                  <p className="muted small">{t("courses.lab_grading")}</p>
                  {labError && (
                    <p className="feedback fail">{labError}</p>
                  )}
                  <button
                    className="btn btn-primary"
                    disabled={labBusy}
                    onClick={() => void openPersonalLab()}
                  >
                    {labBusy ? (
                      <>
                        <Loader2 size={15} className="spin" /> {t("courses.lab_starting")}
                      </>
                    ) : (
                      <>
                        {t("courses.lab_open")} <ChevronRight size={15} />
                      </>
                    )}
                  </button>
                </div>
              )}

              {current.type === "SCRIPT_REVIEW" && (() => {
                const s = (current.content as any).script as {
                  filename?: string;
                  language?: string;
                  code: string;
                  verdict: "SAFE" | "DANGEROUS";
                  severity?: "warning" | "danger";
                  explanation?: string;
                  fix?: string;
                };
                const revealed =
                  !!submitted[current.id] ||
                  current.status === "PASSED" ||
                  current.status === "FAILED";
                const pick = (v: number) =>
                  setChoice((p) => ({ ...p, [current.id]: [v] }));
                return (
                  <div className="lesson-script">
                    <div className="script-frame">
                      <div className="script-titlebar">
                        <span className="script-dots" aria-hidden="true">
                          <i />
                          <i />
                          <i />
                        </span>
                        <code>{s.filename ?? "script.sh"}</code>
                        <span className="script-lang">{s.language ?? "bash"}</span>
                      </div>
                      <pre className="script-pre">
                        <code>{s.code}</code>
                      </pre>
                    </div>
                    {!revealed ? (
                      <div className="script-quiz">
                        <p className="quiz-q">{t("courses.script_q")}</p>
                        <div className="script-quiz-row">
                          <label className={`script-opt safe ${choice[current.id]?.[0] === 0 ? "selected" : ""}`}>
                            <input
                              type="radio"
                              name={current.id}
                              checked={choice[current.id]?.[0] === 0}
                              onChange={() => pick(0)}
                            />
                            <span>✓ {t("courses.script_safe")}</span>
                          </label>
                          <label className={`script-opt danger ${choice[current.id]?.[0] === 1 ? "selected" : ""}`}>
                            <input
                              type="radio"
                              name={current.id}
                              checked={choice[current.id]?.[0] === 1}
                              onChange={() => pick(1)}
                            />
                            <span>✗ {t("courses.script_danger")}</span>
                          </label>
                        </div>
                      </div>
                    ) : (
                      <div className="script-reveal">
                        <div className="script-reveal-badges">
                          <span
                            className={
                              s.verdict === "SAFE"
                                ? "script-verdict safe"
                                : "script-verdict danger"
                            }
                          >
                            {s.verdict === "SAFE"
                              ? `✓ ${t("courses.script_verdict_safe")}`
                              : `✗ ${t("courses.script_verdict_danger")}`}
                          </span>
                          {s.severity === "danger" && (
                            <span className="script-sev sev-danger">
                              ☢ {t("courses.script_sev_danger")}
                            </span>
                          )}
                          {s.severity === "warning" && (
                            <span className="script-sev sev-warning">
                              ⚠ {t("courses.script_sev_warning")}
                            </span>
                          )}
                        </div>
                        {s.explanation && (
                          <p className="script-expl">{s.explanation}</p>
                        )}
                        {s.fix && (
                          <div className="script-frame fix">
                            <div className="script-titlebar">
                              <code>
                                {s.filename ?? "script.sh"} ·{" "}
                                {t("courses.script_fix")}
                              </code>
                            </div>
                            <pre className="script-pre">
                              <code>{s.fix}</code>
                            </pre>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })()}

              {(submitted[current.id] ||
                current.status === "FAILED") && (
                <div
                  className={
                    submitted[current.id]?.status === "PASSED" ||
                    current.status === "PASSED"
                      ? "feedback ok"
                      : submitted[current.id]?.status === "PENDING"
                        ? "feedback pending"
                        : "feedback fail"
                  }
                >
                  {submitted[current.id]?.feedback ??
                    (current.status === "FAILED"
                      ? "Бұрынғы жауап қате болды — қайта көріңіз."
                      : "")}
                </div>
              )}

              <div
                className="inline"
                style={{
                  justifyContent: "space-between",
                  marginTop: "18px",
                }}
              >
                <Button
                  variant="secondary"
                  disabled={page === 0}
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                >
                  ← Алдыңғы
                </Button>
                <div className="inline">
                  {current.status !== "PASSED" && (
                    <Button
                      onClick={submit}
                      disabled={
                        busy ||
                        (current.type === "SCRIPT_REVIEW" &&
                          !choice[current.id]?.length)
                      }
                    >
                      {busy ? <Loader2 size={16} className="spin" /> : <Target size={16} />}
                      {current.type === "THEORY"
                        ? "Оқыдым, келесі"
                        : current.type === "VIDEO"
                          ? "Посмотрел(а)"
                          : current.type === "LAB_TASK"
                            ? "Таныстым"
                            : "Жауап беру"}
                    </Button>
                  )}
                  {page < data.steps.length - 1 && (
                    <Button
                      variant="secondary"
                      onClick={() => setPage((p) => p + 1)}
                    >
                      Келесі қадам →
                    </Button>
                  )}
                </div>
              </div>
            </div>

            {data.status === "COMPLETED" && (
              <div className="card lesson-done">
                <div className="inline">
                  <CheckCircle2 size={20} />
                  <strong>Сабақ аяқталды! +{data.lesson.xp} XP</strong>
                </div>
              </div>
            )}
          </>
        ) : null}
      </DataState>
    </div>
  );
}
